<template>
  <es-loader v-if="loading" style="margin: 5rem"/>
  <es-exception
    v-else-if="loadingError"
    exceptionMessage="There was an issue. Please try again or contact your administrator."
    margin="0"
    style="padding: 5rem"
  />
  <div v-else class="document-container" style="padding: 1rem 0rem">
    <div class="columns is-desktop">
      <div class="column">
        <rule-manager-pi-match-and-assign-rules
          @allowEditOtherPanels="allowEditOtherPanels()"
          :editOtherPanels="editOtherPanels"
          :matchingRules="matchingRules"
          :sortedAvailablePersonTypes="sortedAvailablePersonTypes"
        />
      </div>
      <div class="column">
        <rule-manager-pi-exact-match-rules
          v-if="hiddenAddExactMatchRule && hiddenExactMatchRuleDetails"
          class="margin-bottom"
          @allowEditOtherPanels="allowEditOtherPanels()"
          @toggleAddExactMatchRule="toggleAddExactMatchRule()"
          @toggleExactMatchRuleDetails="toggleExactMatchRuleDetails($event)"
          :editOtherPanels="editOtherPanels"
          :exactMatchRules="exactMatchRules"
          :sortedAvailablePersonTypes="sortedAvailablePersonTypes"
        />
        <rule-manager-pi-add-exact-match-rule
          v-else-if="!hiddenAddExactMatchRule"
          class="margin-bottom"
          @allowEditOtherPanels="allowEditOtherPanels()"
          @toggleExactMatchRules="toggleAddExactMatchRule()"
          :sortedAvailablePersonTypes="sortedAvailablePersonTypes"
        />
        <rule-manager-pi-exact-match-rule-details
          v-else-if="!hiddenExactMatchRuleDetails"
          class="margin-bottom"
          @allowEditOtherPanels="allowEditOtherPanels()"
          @toggleExactMatchRules="toggleExactMatchRuleDetails()"
          :editOtherPanels="editOtherPanels"
          :ruleId="ruleId"
          :sortedAvailablePersonTypes="sortedAvailablePersonTypes"
        />
        <rule-manager-pi-searching-rules
          @allowEditOtherPanels="allowEditOtherPanels()"
          :editOtherPanels="editOtherPanels"
          :searchingRules="searchingRules"
          :sortedAvailablePersonTypes="sortedAvailablePersonTypesSearch"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import RuleManagerPiAddExactMatchRule from '@/components/RuleManagerPiAddExactMatchRule.vue';
import RuleManagerPiExactMatchRules from '@/components/RuleManagerPiExactMatchRules.vue';
import RuleManagerPiExactMatchRuleDetails from '@/components/RuleManagerPiExactMatchRuleDetails.vue';
import RuleManagerPiMatchAndAssignRules from '@/components/RuleManagerPiMatchAndAssignRules.vue';
import RuleManagerPiSearchingRules from '@/components/RuleManagerPiSearchingRules.vue';

export default {
  components: {
    EsException,
    EsLoader,
    RuleManagerPiAddExactMatchRule,
    RuleManagerPiExactMatchRules,
    RuleManagerPiExactMatchRuleDetails,
    RuleManagerPiMatchAndAssignRules,
    RuleManagerPiSearchingRules,
  },
  created() {
    this.onLoad();
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    sortedAvailablePersonTypes() {
      return this.userPersonTypes.filter(p => this.enabledSubmissionAssign
        .includes(p.code)).slice().sort(this.compare);
    },
    sortedAvailablePersonTypesSearch() {
      return this.userPersonTypes.filter(p => this.enabledSubmissionSearch
        .includes(p.code)).slice().sort(this.compare);
    },
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    enabledSubmissionAssign() {
      if (this.allSubSpecsArray) {
        return this.allSubSpecsArray
          .filter(d => d.purpose === 'ASSIGN' && d.enabled === true)
          .map(p => p.personType.code);
      } return null;
    },
    enabledSubmissionSearch() {
      if (this.allSubSpecsArray) {
        return this.allSubSpecsArray
          .filter(d => d.purpose === 'SEARCH' && d.enabled === true)
          .map(p => p.personType.code);
      } return null;
    },
  },
  data() {
    return this.initialData();
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    allowEditOtherPanels() {
      this.editOtherPanels = !this.editOtherPanels;
    },
    compare(a, b) {
      if (a.name.toUpperCase() < b.name.toUpperCase()) {
        return -1;
      }
      if (a.name.toUpperCase() > b.name.toUpperCase()) {
        return 1;
      }
      return 0;
    },
    initialData() {
      return {
        allSubmissionSpecs: [],
        editOtherPanels: false,
        exactMatchRules: [],
        hiddenAddExactMatchRule: true,
        hiddenExactMatchRuleDetails: true,
        loading: false,
        loadingError: false,
        matchingRules: [],
        ruleId: null,
        searchingRules: [],
        userPersonTypes: this.$store.state.user.userPersonTypes
          .map(item => item.personType).filter(a => a.status === true),
      };
    },
    async onLoad() {
      try {
        await Promise.all([
          this.loadPersonTypes(),
        ]);
        this.loading = true;
        this.loadingError = false;
        await this.loadSubmissionSpecs();
        this.matchingRules = [];
        this.searchingRules = [];
        this.exactMatchRules = [];
        this.userPersonTypes.forEach(async (personType) => {
          try {
            if (this.enabledSubmissionAssign) {
              const findPersonTypeCode = this.enabledSubmissionAssign
                .find(p => p === personType.code);
              if (findPersonTypeCode) {
                const res1 = await axios.get(`rule-manager/person-id/matching-engine-rules/personType?ruleType=MATCHING&personTypeCode=${personType.code}`);
                const newArr1 = res1.data.map(v => ({ ...v, hasErr: false, hasErrMsg: null }));
                this.matchingRules.push(newArr1.filter(r => r.isHidden !== 1));
                const res3 = await axios.get(`rule-manager/person-id/exact-match-rules/personType?personTypeCode=${personType.code}`);
                this.exactMatchRules.push(res3.data);
              }
            }
            if (this.enabledSubmissionSearch) {
              const findPersonTypeCode = this.enabledSubmissionSearch
                .find(p => p === personType.code);
              if (findPersonTypeCode) {
                const res2 = await axios.get(`rule-manager/person-id/matching-engine-rules/personType?ruleType=SEARCH_ONLY&personTypeCode=${personType.code}`);
                const newArr2 = res2.data.map(v => ({ ...v, hasErr: false, hasErrMsg: null }));
                this.searchingRules.push(newArr2.filter(r => r.isHidden !== 1));
              }
            }
          } catch (err) {
            this.loadingError = true;
          }
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async loadSubmissionSpecs() {
      const personTypeIds = this.userPersonTypes.map(item => item.id);
      try {
        this.loading = true;
        this.loadingError = false;
        // build promises to get submission specs info per person type
        const submissionSpecResponse = await Promise.all([...personTypeIds]
          .map(id => axios.get(`person-types/${id}/submission-specs/`)));
        // use the response from above and push them
        submissionSpecResponse.forEach((s) => {
          this.allSubmissionSpecs.push(s.data);
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    toggleAddExactMatchRule() {
      this.onLoad();
      this.hiddenAddExactMatchRule = !this.hiddenAddExactMatchRule;
    },
    toggleExactMatchRuleDetails(id) {
      this.ruleId = id;
      this.onLoad();
      this.hiddenExactMatchRuleDetails = !this.hiddenExactMatchRuleDetails;
    },
  },
};
</script>

<style lang="sass" scoped>
  .margin-bottom
    margin-bottom: 1rem
</style>
